// Landed vs raw-ingredient dish cost for the current site (F6b).
//   GET /api/costing/menu-engineering/landed?site_id=<current site>
// "Raw" expands CK items to supplier prices; "landed" uses what the site paid the CK
// (the cost on the transfer lots it received).
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { apiBase, authHeaders } from '../utils/authHeaders';
import { apiErrorMessage } from '../utils/apiError';
import { fmtUnitCost } from '../utils/unitCost';
import { fmtPctPoints } from './RecostAlerts';
import { formatMoney } from '../utils/money';

const fmtGbp = v => (v === null || v === undefined ? '—' : formatMoney(Number(v)));
const readSite = () => { try { return localStorage.getItem('current_location_id') || ''; } catch { return ''; } };

export default function LandedVsRaw() {
  const [site] = useState(readSite);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    if (!site) return;
    setLoading(true); setError('');
    try {
      const res = await fetch(`${apiBase()}/api/costing/menu-engineering/landed?site_id=${encodeURIComponent(site)}`,
        { headers: authHeaders({ locationId: site }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(apiErrorMessage(body.detail, res.statusText || 'Could not load landed costs.'));
      setReport(body);
    } catch (e) { setReport(null); setError(e.message || String(e)); }
    finally { setLoading(false); }
  }, [site]);
  useEffect(() => { load(); }, [load]);

  if (!site) return <p role="status" className="p-8 text-center text-sm text-ink-muted">Select a site to compare landed and raw dish cost.</p>;
  return (
    <section className="space-y-4" data-testid="landed-vs-raw" aria-label="Landed vs raw cost">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl text-ink">Landed vs raw cost</h2>
          <p className="text-sm text-ink-muted">Landed uses the cost of CK items this site received (weighted over lots on hand, else the latest lot).</p>
        </div>
        <button onClick={load} disabled={loading} aria-label="Refresh landed costs" className="px-3 py-1.5 border border-surface-border rounded-md"><RefreshCw className="w-4 h-4" /></button>
      </div>
      {error && <div role="alert" className="bg-danger/5 border border-danger/30 text-danger rounded-md p-3 text-sm flex gap-2"><AlertTriangle className="w-4 h-4" />{error}</div>}
      {loading && <div role="status" className="p-8 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>}
      {report && !loading && <>
        {report.landed_items?.length ? <p className="text-xs text-ink-muted" data-testid="landed-items">
          Received from CK: {report.landed_items.map(i => `${i.name || i.item_id} ${fmtUnitCost(i.landed_unit_cost, i.unit)}`).join(' · ')}
        </p> : <p className="text-xs text-ink-muted">No CK transfer lots received at this site yet; landed equals raw.</p>}
        {report.warnings?.length ? <div className="text-sm text-warn">{report.warnings.join(' ')}</div> : null}
        <table className="w-full text-sm bg-surface border border-surface-border rounded-lg">
          <thead className="text-xs text-ink-muted uppercase bg-surface-sunken"><tr>
            <th className="text-left px-3 py-2">Dish</th><th className="text-right px-3">Price</th>
            <th className="text-right px-3">Raw cost</th><th className="text-right px-3">Raw GP</th>
            <th className="text-right px-3">Landed cost</th><th className="text-right px-3">Landed GP</th><th className="text-right px-3">GP change</th>
          </tr></thead>
          <tbody>{report.items.map(row => (
            <tr key={row.menu_item_id} className="border-t border-surface-border" data-testid={`landed-row-${row.menu_item_id}`}>
              <td className="px-3 py-2">{row.name}{row.warnings?.length ? <div className="text-xs text-warn">{row.warnings[0]}</div> : null}</td>
              <td className="text-right px-3 font-data">{fmtGbp(row.selling_price)}</td>
              <td className="text-right px-3 font-data" title={row.raw?.per_portion_cost || ''}>{fmtGbp(row.raw?.per_portion_cost)}</td>
              <td className="text-right px-3 font-data">{fmtPctPoints(row.raw?.gp_pct)}</td>
              <td className="text-right px-3 font-data" title={row.landed?.per_portion_cost || ''}>{fmtGbp(row.landed?.per_portion_cost)}</td>
              <td className="text-right px-3 font-data">{fmtPctPoints(row.landed?.gp_pct)}</td>
              <td className="text-right px-3 font-data">{fmtPctPoints(row.gp_delta)}</td>
            </tr>))}</tbody>
        </table>
      </>}
    </section>
  );
}
